/**
 * useResumeFlow — recovery lifecycle for a FAILED step.
 *
 * When an agent crashes mid-run, the workflow lands in status=failed
 * with the completed agents' outputs safe in the LangGraph checkpoint.
 * `POST /workflow/resume` (which accepts FAILED workflows) re-enqueues
 * the run; the backend clears the failed agent's stale bookkeeping and
 * the graph re-runs ONLY the crashed agent from the last checkpoint —
 * no completed work is redone.
 *
 * `agentName` is the failed step's own agent (unlike useApproveFlow,
 * which targets the NEXT agent): routing is actually driven by the
 * checkpoint's `next_agent`, and the message's agent_name keeps the
 * progress bookkeeping (reset + mark-started) pointed at the right slot.
 *
 * Mirrors useApproveFlow's conventions: mountedRef gates, refs for
 * refetch/actions, optimistic step-status write, 500ms held-flag
 * refetch, toast on error.
 */
import { useCallback, useRef, useState } from 'react';
import { resumeWorkflow } from '../../../api/workflow';
import { findStep } from '../state/workflowV2StepCatalog';
import { notify } from '../../../services/notificationBus';
import { useMountedRef } from '../../../hooks/useMountedRef';
import type { WorkflowActions } from '../state/workflowV2Types';

export interface UseResumeFlowInput {
  stepId: number;
  threadId: string | null | undefined;
  sessionId: string | null | undefined;
  tenantId: string | null | undefined;
  refetchStatus: () => void;
  actions?: WorkflowActions;
}

export interface ResumeFlowState {
  resuming: boolean;
  error: string | null;
  onResume: () => Promise<void>;
}

export function useResumeFlow({
  stepId,
  threadId,
  sessionId,
  tenantId,
  refetchStatus,
  actions,
}: UseResumeFlowInput): ResumeFlowState {
  const [resuming, setResuming] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const refetchRef = useRef(refetchStatus);
  refetchRef.current = refetchStatus;
  const actionsRef = useRef(actions);
  actionsRef.current = actions;
  const mountedRef = useMountedRef();
  // Re-entry guard that doesn't wait for the state flush — protects
  // against a rapid double-click before `resuming` re-renders the button.
  const inFlightRef = useRef(false);

  const onResume = useCallback(async () => {
    if (inFlightRef.current) return;
    if (!threadId || !sessionId || !tenantId) {
      setError('Missing thread / session / tenant context.');
      return;
    }
    const agentName = findStep(stepId)?.agent;
    if (!agentName) {
      setError(`No agent for step ${stepId}.`);
      return;
    }
    inFlightRef.current = true;
    setResuming(true);
    setError(null);
    try {
      await resumeWorkflow({
        threadId,
        sessionId,
        tenantId,
        agentName,
        userFeedback: '',
      });
      if (!mountedRef.current) return;
      // Optimistic: the failed step is re-running. Polling reconciles —
      // the backend resets the agent to pending/in_progress on resume.
      actionsRef.current?.setStepStatus(stepId, 'generating');
      // Hold the resuming flag until the refetch fires so the button
      // never briefly re-enables (mirrors useApproveFlow).
      setTimeout(() => {
        if (!mountedRef.current) return;
        refetchRef.current?.();
        setResuming(false);
        inFlightRef.current = false;
      }, 500);
    } catch (e: any) {
      const msg = e?.response?.data?.detail || e?.message || 'Failed to resume analysis';
      if (!mountedRef.current) return;
      setError(msg);
      notify({ type: 'error', title: 'Resume failed', message: msg });
      setResuming(false);
      inFlightRef.current = false;
    }
  }, [stepId, threadId, sessionId, tenantId]);

  return { resuming, error, onResume };
}
